import * as pdfjsLib from 'pdfjs-dist';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist/types/src/display/api';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.mjs',
  import.meta.url
).toString();

type SignaturePercentPosition = {
  xPercentage: number;
  yPercentage: number;
  widthPercentage: number;
  heightPercentage: number;
};

const renderScale = 1.5;

async function renderPdf(
  url: string,
  canvasEl: HTMLCanvasElement,
  containerEl: HTMLElement,
  signatureEl: HTMLImageElement,
): Promise<void> {
  const loadingTask = pdfjsLib.getDocument(url);
  const pdf: PDFDocumentProxy = await loadingTask.promise;

  const inputPageToSignEl = document.querySelector('#id_page_to_sign') as HTMLInputElement;

  const pageToSign = inputPageToSignEl?.value ? Number(inputPageToSignEl.value) : 1;

  const page: PDFPageProxy = await pdf.getPage(pageToSign);
  const viewport = page.getViewport({ scale: renderScale });

  const ctx = canvasEl.getContext('2d');
  if (!ctx) {
    throw new Error('No se pudo obtener el contexto 2D del canvas');
  }

  const canvas = canvasEl;

  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);

  await page.render({
    canvasContext: ctx,
    viewport,
  }).promise;

  document.addEventListener('mouseup', () => {
    const position = getSignaturePercentPosition(signatureEl, canvasEl);

    const inputXPercentageEl = document.querySelector('#id_signature_x_percentage') as HTMLInputElement;
    const inputYPercentageEl = document.querySelector('#id_signature_y_percentage') as HTMLInputElement;
    const inputWidthPercentageEl = document.querySelector('#id_signature_width_percentage') as HTMLInputElement;

    if (inputXPercentageEl) {
      inputXPercentageEl.value = position.xPercentage.toString();
    }

    if (inputYPercentageEl) {
      inputYPercentageEl.value = position.yPercentage.toString();
    }

    if (inputWidthPercentageEl) {
      inputWidthPercentageEl.value = position.widthPercentage.toString();
    }
  });
}

function applyInitialSignaturePositionFromInputs(
  canvasEl: HTMLCanvasElement,
  signatureEl: HTMLImageElement,
): void {
  const inputXPercentageEl = document.querySelector('#id_signature_x_percentage') as HTMLInputElement;
  const inputYPercentageEl = document.querySelector('#id_signature_y_percentage') as HTMLInputElement;
  const inputWidthPercentageEl = document.querySelector('#id_signature_width_percentage') as HTMLInputElement;

  const xPercentage = inputXPercentageEl?.value ? Number(inputXPercentageEl.value) : 0;
  const yPercentage = inputYPercentageEl?.value ? Number(inputYPercentageEl.value) : 0;
  const widthPercentage = (
    inputWidthPercentageEl?.value ? Number(inputWidthPercentageEl.value) : 0.2
  );

  if (
    Number.isNaN(xPercentage)
      || Number.isNaN(yPercentage)
      || Number.isNaN(widthPercentage)
  ) {
    return;
  }

  const canvasRect = canvasEl.getBoundingClientRect();

  const widthPx = canvasRect.width * widthPercentage;
  const aspectRatio = (
    signatureEl.naturalWidth > 0
      ? signatureEl.naturalHeight / signatureEl.naturalWidth
      : 0.5
  );

  const heightPx = widthPx * aspectRatio;

  let leftPx = canvasRect.width * xPercentage;
  let topPx = canvasRect.height * yPercentage;

  // Limitar para que no se salga del área visible
  const maxLeft = canvasEl.clientWidth - widthPx;
  const maxTop = canvasEl.clientHeight - heightPx;

  leftPx = Math.max(0, Math.min(leftPx, maxLeft));
  topPx = Math.max(0, Math.min(topPx, maxTop));

  const signature = signatureEl;

  signature.style.position = 'absolute';
  signature.style.width = `${widthPx}px`;
  signature.style.height = `${heightPx}px`;
  signature.style.left = `${leftPx}px`;
  signature.style.top = `${topPx}px`;
}

function getSignaturePercentPosition(
  signatureEl: HTMLImageElement,
  canvasEl: HTMLCanvasElement,
): SignaturePercentPosition {
  const signatureRect = signatureEl.getBoundingClientRect();
  const canvasRect = canvasEl.getBoundingClientRect();

  const cssX = signatureRect.left - canvasRect.left;
  const cssY = signatureRect.top - canvasRect.top;
  const cssWidth = signatureRect.width;
  const cssHeight = signatureRect.height;

  return {
    xPercentage: cssX / canvasRect.width,
    yPercentage: cssY / canvasRect.height,
    widthPercentage: cssWidth / canvasRect.width,
    heightPercentage: cssHeight / canvasRect.height,
  };
}

function makeSignatureDraggable(
  containerEl: HTMLElement,
  signatureEl: HTMLImageElement,
): void {
  let isDragging = false;
  let offsetX = 0;
  let offsetY = 0;

  signatureEl.addEventListener('dragstart', (event) => {
    event.preventDefault();
  });

  signatureEl.addEventListener('mousedown', (event: MouseEvent) => {
    event.preventDefault();

    const signatureRect = signatureEl.getBoundingClientRect();

    isDragging = true;
    offsetX = event.clientX - signatureRect.left;
    offsetY = event.clientY - signatureRect.top;
  });

  document.addEventListener('mousemove', (event: MouseEvent) => {
    if (!isDragging) {
      return;
    }

    const containerRect = containerEl.getBoundingClientRect();

    let newLeft = event.clientX - containerRect.left - offsetX;
    let newTop = event.clientY - containerRect.top - offsetY;

    const maxLeft = containerEl.clientWidth - signatureEl.offsetWidth;
    const maxTop = containerEl.clientHeight - signatureEl.offsetHeight;

    if (newLeft < 0) {
      newLeft = 0;
    }
    if (newTop < 0) {
      newTop = 0;
    }
    if (newLeft > maxLeft) {
      newLeft = maxLeft;
    }
    if (newTop > maxTop) {
      newTop = maxTop;
    }

    const signature = signatureEl;
    signature.style.left = `${newLeft}px`;
    signature.style.top = `${newTop}px`;
  });

  document.addEventListener('mouseup', () => {
    isDragging = false;
  });
}

window.addEventListener('DOMContentLoaded', () => {
  const canvasEl = document.querySelector('#canvas-pdf-signer-preview');
  const containerEl = document.querySelector('#pdf-signer-container');
  const signatureEl = document.querySelector('#signature-preview');

  if (!canvasEl && !containerEl && !signatureEl) {
    return;
  }

  if (!(canvasEl instanceof HTMLCanvasElement)) {
    throw new Error('El elemento #canvas-pdf-signer-preview no es un <canvas>');
  }

  if (!(containerEl instanceof HTMLElement)) {
    throw new Error('El elemento #pdf-signer-container no existe');
  }

  if (!(signatureEl instanceof HTMLImageElement)) {
    throw new Error('El elemento #signature-preview no es una imagen');
  }

  const pdfUrl = containerEl.getAttribute('data-pdf-url');
  if (!pdfUrl) {
    throw new Error('El contenedor del PDF signer no tiene el atributo data-pdf-url');
  }

  renderPdf(pdfUrl, canvasEl, containerEl, signatureEl)
    .then(() => {
      applyInitialSignaturePositionFromInputs(canvasEl, signatureEl);
      makeSignatureDraggable(containerEl, signatureEl);
    })
    .catch((error: unknown) => {
      // eslint-disable-next-line no-console
      console.error('Error al renderizar el PDF:', error);
    });
});
